import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { compose } from "redux";
import { injectIntl } from "react-intl";
import { Container } from "@domestika-dev/prudencio/dist/atoms/Layout";
import { Portlet } from "@domestika-dev/prudencio/dist/atoms/Portlet";

import { getCaptchaToken } from "../../../packs/grecaptcha";
import NotificationsAlerts from "../../utils/notificationsAlerts";
import { useScrollPosition } from "../../../js/modules/hooks/scroll";
import { useDeviceDetection } from "../../../js/modules/hooks/deviceDetection";
import injectReducer from "../../utils/reducers/injectReducer";
import injectSaga from "../../utils/saga/injectSaga";
import { contentItemsSummary } from "../utils";
import {
  setProject,
  setProjectStore,
  setChangeContentItem,
  setCreateContentItem,
  setRemoveContentItem,
  getProject,
  getCourse,
  deleteProject,
  setUpdateContentItemsPosition,
  setImageStartLoading,
  setImageStopLoading,
  setCoverStartLoading,
  setCoverStopLoading,
  setEmbedStartLoading,
  setEmbedStopLoading,
} from "../actions";
import { ProjectForm, LoaderModal, DeleteModal, OrganizeModal } from "../component";
import reducer from "../reducer";
import saga from "../saga";
import { NO_FOCUS } from "../config";

const ProjectPage = ({ projectId, courseId, suggestedName }) => {
  const dispatch = useDispatch();

  const [showOnScroll, setShowOnScroll] = useState(false);
  const [showOnScrollTop, setShowOnScrollTop] = useState(0);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isOrganizeModalOpen, setOrganizeModalOpen] = useState(false);
  const [focusedElement, setFocusedElement] = useState({ id: NO_FOCUS });

  const project = useSelector(state => state.projects);

  const { isMobile } = useDeviceDetection();

  useScrollPosition(
    ({ prevPos, currPos }) => {
      const contentItemMenuParent = document.querySelector("#ContentItemMenu")?.parentElement;
      const scrollShow =
        contentItemMenuParent?.offsetTop + contentItemMenuParent?.scrollHeight + 96 || 0;

      const projectForm = document.querySelector("[data-label='project-form']");
      const scrollHideOutForm = projectForm?.offsetTop + projectForm?.scrollHeight + 96 || 0;

      const navigation = document.querySelector("#js-mainnav");

      setShowOnScrollTop(navigation.classList.contains("slideDown") ? navigation.scrollHeight : 0);

      const isShow = Math.abs(currPos.y) > scrollShow && Math.abs(currPos.y) < scrollHideOutForm;

      if (isShow !== showOnScroll) setShowOnScroll(isShow);
    },
    [showOnScroll]
  );

  const handleSubmit = useCallback(
    async (project, publish, recaptchaVersion) => {
      try {
        const token = await getCaptchaToken(recaptchaVersion);

        dispatch(setProject(project, publish, token));
      } catch (error) {
        let errorMessage = "error saving project as draft";

        if (publish) {
          errorMessage = "error publishing project";
        }

        const customError = new Error(`${errorMessage}: ${error}`);
        customError.context = {
          project: project,
          recaptchaVersion: recaptchaVersion,
        };

        throw customError;
      }
    },
    [dispatch]
  );

  const handleUpdateLocalStore = useCallback(data => dispatch(setProjectStore(data)), [dispatch]);

  const handleDeleteProject = useCallback(idProject => dispatch(deleteProject(idProject)), [
    dispatch,
  ]);

  const onChangeContentItemStore = useCallback(
    (elementId, key, value) => dispatch(setChangeContentItem(elementId, key, value)),
    [dispatch]
  );

  const onCreateContentItemStore = useCallback(
    elementType => dispatch(setCreateContentItem(elementType)),
    [dispatch]
  );

  const onRemoveContentItemStore = useCallback(
    contentItemId => dispatch(setRemoveContentItem(contentItemId)),
    [dispatch]
  );

  const onUpdateContentItemsPositions = useCallback(
    newOrder => {
      dispatch(setUpdateContentItemsPosition(newOrder));
    },
    [dispatch]
  );

  const onImageStartLoading = useCallback(() => dispatch(setImageStartLoading()), [dispatch]);

  const onImageStopLoading = useCallback(() => dispatch(setImageStopLoading()), [dispatch]);

  const onCoverStartLoading = useCallback(() => dispatch(setCoverStartLoading()), [dispatch]);

  const onCoverStopLoading = useCallback(() => dispatch(setCoverStopLoading()), [dispatch]);

  const onEmbedStartLoading = useCallback(() => dispatch(setEmbedStartLoading()), [dispatch]);

  const onEmbedStopLoading = useCallback(() => dispatch(setEmbedStopLoading()), [dispatch]);

  useEffect(() => {
    if (projectId) {
      dispatch(getProject(projectId));
    }
  }, [projectId]);

  useEffect(() => {
    const currentCourseId = project.courseId || courseId;

    if (currentCourseId) {
      dispatch(getCourse(currentCourseId));
    }
  }, [project.courseId, courseId]);

  useEffect(() => {
    if (suggestedName) {
      dispatch(handleUpdateLocalStore({ name: suggestedName }));
    }
  }, [suggestedName]);

  if (project.loading) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";
  }

  /**
    Trick to avoid the scroll of the page with the OrganizeModal open in mobile devices
    (it crashes with the DraggableList scroll)
  */
  const openOrganizeModal = () => {
    if (isMobile) {
      document.body.style.position = "fixed";
    }

    setOrganizeModalOpen(true);
  };

  const closeOrganizeModal = () => {
    document.body.style.position = "";
    setOrganizeModalOpen(false);
  };

  return (
    <>
      <DeleteModal
        isOpen={isDeleteModalOpen}
        setOpen={setDeleteModalOpen}
        confirmAction={() => handleDeleteProject(project.id)}
      />

      <OrganizeModal
        contentItems={contentItemsSummary(project.contentItems)}
        isOpen={isOrganizeModalOpen}
        onClose={closeOrganizeModal}
        onFocusElement={setFocusedElement}
        confirmAction={onUpdateContentItemsPositions}
      />

      <NotificationsAlerts />

      <LoaderModal isOpen={project.loading} messageKey="projects.new.saving.confirmation_message" />

      <Container px={0}>
        <ProjectForm
          isMobile={isMobile}
          handleUpdateLocalStore={handleUpdateLocalStore}
          onChangeContentItemStore={onChangeContentItemStore}
          onCreateContentItemStore={onCreateContentItemStore}
          onDeleteProjectModal={setDeleteModalOpen}
          onEmbedStartLoading={onEmbedStartLoading}
          onEmbedStopLoading={onEmbedStopLoading}
          onImageStartLoading={onImageStartLoading}
          onImageStopLoading={onImageStopLoading}
          onCoverStartLoading={onCoverStartLoading}
          onCoverStopLoading={onCoverStopLoading}
          onRemoveContentItemStore={onRemoveContentItemStore}
          onSaveProject={handleSubmit}
          onSortContentItems={openOrganizeModal}
          onFocusElement={setFocusedElement}
          focusedElement={focusedElement}
          showOnScroll={showOnScroll}
          showOnScrollTop={showOnScrollTop}
        />
      </Container>
    </>
  );
};

ProjectPage.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  courseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  suggestedName: PropTypes.string,
};

ProjectPage.defaultProps = {
  projectId: null,
  courseId: null,
  suggestedName: "",
};

const withReducer = injectReducer({ key: "projects", reducer });
const withSaga = injectSaga({ key: "projects", saga });

export default compose(withReducer, withSaga)(injectIntl(ProjectPage));
